import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Star } from "lucide-react";
import { RevealHeading, Reveal } from "../components/site/Reveal";
import { Marquee } from "../components/site/Decor";
import {
  OrnateDivider,
  Medallion,
} from "../components/site/Ornaments";
import {
  BUSINESS,
  IMAGES,
  SERVICES,
  TESTIMONIALS,
  whatsappLink,
} from "../data/site";

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        ref={heroRef}
        className="relative min-h-[100svh] overflow-hidden pt-44"
      >
        <div className="toran pointer-events-none absolute inset-x-0 top-[96px] z-0 opacity-90" aria-hidden="true" />
        <div className="relative z-10 mx-auto grid max-w-[1400px] grid-cols-1 items-end gap-8 px-5 pb-16 sm:px-8 lg:grid-cols-12 lg:px-12">
          <motion.div style={{ y: textY }} className="lg:col-span-7">
            <Reveal delay={0.1}>
              <div className="flex items-center gap-3 text-henna">
                <Medallion size={26} />
                <span className="text-xs uppercase tracking-[0.3em]">
                  Bridal Henna · Pathankot · Est. {BUSINESS.established}
                </span>
              </div>
            </Reveal>
            <Reveal delay={0.18}>
              <span className="mt-4 block font-serif text-2xl italic text-maroon sm:text-3xl">
                Best Mehndi Artist in Pathankot
              </span>
            </Reveal>
            <RevealHeading
              testId="hero-title"
              className="mt-3 font-display text-[16vw] leading-[0.86] tracking-tight sm:text-[13vw] lg:text-[8.5vw]"
              delay={0.25}
              lines={[
                <>Beautiful mehndi</>,
                <>
                  for your <span className="italic text-henna">big day</span>
                </>,
              ]}
            />
            <Reveal delay={0.9}>
              <p className="mt-8 max-w-md text-base leading-relaxed text-ink/70">
                Two decades of freehand bridal &amp; festive mehndi, drawn with
                a steady hand and old-world patience in the heart of Pathankot.
                Rated {BUSINESS.rating}★ by {BUSINESS.reviews} brides and
                families.
              </p>
            </Reveal>
            <Reveal delay={1.05}>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <a
                  href={whatsappLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-book-btn"
                  className="group inline-flex items-center gap-2 rounded-full bg-henna px-8 py-4 text-xs uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-ink"
                >
                  Book your date
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
                <Link
                  to="/gallery"
                  data-testid="hero-gallery-link"
                  className="link-underline text-xs uppercase tracking-[0.2em] text-ink"
                >
                  View the gallery
                </Link>
              </div>
            </Reveal>
          </motion.div>

          <div className="lg:col-span-5">
            <div className="relative h-[52vh] overflow-hidden arch-top lg:h-[74vh]">
              <motion.img
                src={IMAGES.heroPortrait}
                alt="Bride showcasing intricate bridal mehndi"
                style={{ y: imgY, scale: imgScale }}
                className="img-cover"
                data-testid="hero-image"
              />
              <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-ink/10 arch-top" />
            </div>
            <div className="mt-4 flex items-center justify-between text-xs uppercase tracking-[0.15em] text-ink/50">
              <span>Freehand · Natural henna</span>
              <span className="flex items-center gap-1">
                <Star className="h-3 w-3 fill-gold text-gold" /> {BUSINESS.rating}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="bg-cashmere">
        <div className="saree-border" aria-hidden="true" />
        <div className="py-8">
          <Marquee
            items={["Bridal Mehndi", "Arabic", "Engagement", "Festive", "Karva Chauth"]}
          />
        </div>
        <div className="saree-border rotate-180" aria-hidden="true" />
      </section>

      {/* INTRO / STATS */}
      <section className="mx-auto max-w-[1400px] px-5 py-24 sm:px-8 lg:px-12">
        <OrnateDivider className="mb-16" />
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <h2 className="font-display text-4xl leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
              A quiet shop in Gandhi Chowk where every bride leaves with a piece
              of <span className="italic font-serif text-henna">wearable art</span>.
            </h2>
          </Reveal>
          <div className="grid grid-cols-2 gap-8 self-end lg:col-span-5">
            {[
              { k: BUSINESS.yearsExperience, v: "Years of craft" },
              { k: BUSINESS.reviews + "+", v: "Google reviews" },
              { k: BUSINESS.rating, v: "Average rating" },
              { k: "10000+", v: "Brides adorned" },
            ].map((s, i) => (
              <Reveal key={s.v} delay={i * 0.08}>
                <p className="font-display text-5xl text-henna lg:text-6xl">
                  {s.k}
                </p>
                <p className="mt-1 text-xs uppercase tracking-[0.18em] text-ink/50">
                  {s.v}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES TEASER */}
      <section className="bg-ink text-bone">
        <div className="mx-auto max-w-[1400px] px-5 py-24 sm:px-8 lg:px-12">
          <div className="flex items-end justify-between">
            <div>
              <h2 className="font-display text-4xl tracking-tight sm:text-5xl">
                What we do
              </h2>
            </div>
            <Link
              to="/services"
              data-testid="home-services-link"
              className="link-underline text-xs uppercase tracking-[0.2em]"
            >
              All services
            </Link>
          </div>
          <div className="mt-12 divide-y divide-bone/15 border-y border-bone/15">
            {SERVICES.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.05}>
                <Link
                  to="/services"
                  className="group flex items-center justify-between gap-6 py-7"
                  data-testid={`home-service-${s.id}`}
                >
                  <div className="flex items-baseline gap-5">
                    <span className="text-xs text-gold">{s.index}</span>
                    <span className="font-serif text-3xl transition-transform duration-500 group-hover:translate-x-2 sm:text-4xl">
                      {s.title}
                    </span>
                  </div>
                  <span className="hidden text-sm text-bone/60 sm:block">
                    {s.price}
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="mx-auto max-w-[1400px] px-5 py-24 text-center sm:px-8 lg:px-12">
        <OrnateDivider className="mb-6" />
        <span className="mt-1 block text-xs uppercase tracking-[0.3em] text-henna">
          Kind words from our brides
        </span>
        <div className="mt-12 grid gap-10 text-left md:grid-cols-3">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.1}>
              <div className="border-t border-ink/15 pt-6">
                <div className="mb-4 flex gap-1">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="h-4 w-4 fill-gold text-gold" />
                  ))}
                </div>
                <p className="font-serif text-2xl leading-snug">
                  “{t.quote}”
                </p>
                <p className="mt-5 text-sm font-medium">{t.name}</p>
                <p className="text-xs uppercase tracking-[0.15em] text-ink/40">
                  {t.meta}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Home;
